import { useEffect, useState, useRef } from "react";
import { api, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, ExternalLink, Plus, Trash2 } from "lucide-react";

const DEFAULTS = {
  hero_eyebrow: "The Instagram concept store",
  hero_title: "Shop the feed. Every store, one experience.",
  hero_subtitle: "Browse independent storefronts built like your favourite Instagram profiles — stories, grids, and instant checkout.",
  cta_primary_label: "Explore stores",
  cta_secondary_label: "Become a merchant",
  stores_heading: "Featured stores",
  footer_tagline: "The Instagram concept store platform.",
  hero_image: "",
  reviews_heading: "Loved by merchants",
  reviews: [
    { name: "Maya Chen", handle: "@mayaruns", text: "Set up my whole shop in an afternoon. My sales tripled in the first month.", rating: 5 },
    { name: "Liam Porter", handle: "@solestories", text: "It genuinely feels like Instagram — my customers just get it.", rating: 5 },
    { name: "Noor Haddad", handle: "@glowbynoor", text: "Managing three storefronts from one dashboard is a dream.", rating: 5 },
  ],
  pricing_heading: "Simple pricing",
  pricing: [
    { id: "starter", name: "Starter", price: "€0", period: "/mo", features: ["1 store", "Up to 20 products", "Real Stripe checkout"], cta: "Get started", highlight: false },
    { id: "growth", name: "Growth", price: "€29", period: "/mo", features: ["Unlimited stores", "Unlimited products", "Live analytics", "Priority support"], cta: "Start free trial", highlight: true },
    { id: "scale", name: "Scale", price: "€99", period: "/mo", features: ["Everything in Growth", "Custom domain", "Team accounts", "API access"], cta: "Contact sales", highlight: false },
  ],
  disclaimer: "StoreIG is an independent platform and is not affiliated with, endorsed by, sponsored by, or in any way officially connected to Instagram or Meta Platforms, Inc.",
  subscriptions_enabled: false,
  frontpage_template: "v2",
  hero_video: "",
  new_stores_heading: "New stores",
  categories_heading: "Find stores by category",
  signup_heading: "Easy sign up",
  signup_subtitle: "Launch your storefront in minutes. No code, no hassle — just your brand, online.",
  signup_video: "",
  signup_steps: [
    { title: "Create your account", text: "Sign up with email or Google in seconds." },
    { title: "Add your products", text: "Upload photos, set prices — done." },
    { title: "Share & start selling", text: "Real Stripe checkout from day one." },
  ],
  brand_name: "CasperBrands",
  brand_url: "https://casperbrands.com",
  brand_logo_url: "https://customer-assets-agu9un31.emergentagent.net/job_store-dashboard-69/artifacts/z5d0msg2_LOGO-CASPERBRANDs.png",
  meta_title: "StoreIG — The Instagram Concept Store",
  meta_description: "Discover independent Instagram-style storefronts on StoreIG. Shop the feed — every store, one seamless experience.",
  og_image: "",
  favicon: "",
};

export default function FrontpagePage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const brandFileRef = useRef();
  const [brandUploading, setBrandUploading] = useState(false);
  const ogFileRef = useRef();
  const [ogUploading, setOgUploading] = useState(false);
  const faviconFileRef = useRef();
  const [faviconUploading, setFaviconUploading] = useState(false);

  useEffect(() => {
    api.get("/public/site").then((r) => {
      const merged = { ...DEFAULTS, ...r.data };
      // backfill stable plan ids so email subscribe deep-links stay valid on reorder
      merged.pricing = (merged.pricing || []).map((p, i) => ({
        ...p, id: p.id || (p.name ? p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") : `plan-${i}`),
      }));
      setForm(merged);
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadHero = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("hero_image", res.data.url);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const uploadBrand = async (file) => {
    setBrandUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("brand_logo_url", res.data.url);
    } catch { toast.error("Upload failed"); }
    finally { setBrandUploading(false); }
  };

  const uploadOg = async (file) => {
    setOgUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("og_image", res.data.url);
    } catch { toast.error("Upload failed"); }
    finally { setOgUploading(false); }
  };

  // reviews helpers
  const setReview = (i, k, v) => setForm((f) => {
    const reviews = [...f.reviews];
    reviews[i] = { ...reviews[i], [k]: v };
    return { ...f, reviews };
  });
  const addReview = () => setForm((f) => ({ ...f, reviews: [...f.reviews, { name: "", handle: "", text: "", rating: 5 }] }));
  const removeReview = (i) => setForm((f) => ({ ...f, reviews: f.reviews.filter((_, x) => x !== i) }));

  // pricing helpers
  const setPrice = (i, k, v) => setForm((f) => {
    const pricing = [...f.pricing];
    pricing[i] = { ...pricing[i], [k]: v };
    return { ...f, pricing };
  });

  // signup steps helpers
  const setStep = (i, k, v) => setForm((f) => {
    const steps = [...(f.signup_steps || [])];
    steps[i] = { ...steps[i], [k]: v };
    return { ...f, signup_steps: steps };
  });
  const addStep = () => setForm((f) => ({ ...f, signup_steps: [...(f.signup_steps || []), { title: "", text: "" }] }));
  const removeStep = (i) => setForm((f) => ({ ...f, signup_steps: (f.signup_steps || []).filter((_, x) => x !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site", form);
      toast.success("Front page updated");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="flex items-center justify-center h-96"><Loader2 className="animate-spin text-zinc-400" /></div>;

  return (
    <div className="animate-fade-up max-w-3xl pb-16" data-testid="frontpage-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-zinc-500">Content</span>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-1">Front page</h1>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm font-semibold border border-zinc-200 rounded-full px-4 py-2 hover:bg-zinc-50" data-testid="frontpage-preview-link">
            <ExternalLink size={14} /> Preview
          </a>
          <button onClick={save} disabled={saving} data-testid="fp-save" className="casper-btn rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />} Save changes
          </button>
        </div>
      </div>

      {/* Hero */}
      <Card title="Hero">
        <F label="Eyebrow"><input data-testid="fp-eyebrow" value={form.hero_eyebrow} onChange={(e) => set("hero_eyebrow", e.target.value)} className="fin" /></F>
        <F label="Title (Enter for line breaks)"><textarea data-testid="fp-title" rows={2} value={form.hero_title} onChange={(e) => set("hero_title", e.target.value)} className="fin" /></F>
        <F label="Subtitle"><textarea data-testid="fp-subtitle" rows={3} value={form.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} className="fin" /></F>
        <div className="grid grid-cols-2 gap-4">
          <F label="Primary button"><input data-testid="fp-cta1" value={form.cta_primary_label} onChange={(e) => set("cta_primary_label", e.target.value)} className="fin" /></F>
          <F label="Secondary button"><input data-testid="fp-cta2" value={form.cta_secondary_label} onChange={(e) => set("cta_secondary_label", e.target.value)} className="fin" /></F>
        </div>
        <F label="Hero background image">
          <div className="flex items-center gap-3">
            <div className="h-16 w-28 rounded-lg overflow-hidden bg-zinc-100 shrink-0">
              {form.hero_image ? <img src={imgUrl(form.hero_image)} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center text-[10px] text-zinc-400">Default</div>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadHero(e.target.files[0])} data-testid="fp-hero-input" />
            <button onClick={() => fileRef.current.click()} className="flex items-center gap-2 border border-zinc-200 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-zinc-50" data-testid="fp-hero-upload">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
            </button>
            {form.hero_image ? <button onClick={() => set("hero_image", "")} className="text-xs text-zinc-400 hover:text-rose-500">Reset</button> : null}
          </div>
        </F>
        <F label="Hero video URL — v2 dark template (leave blank for default; overrides image)">
          <div className="flex items-center gap-2">
            <input data-testid="fp-hero-video" value={form.hero_video || ""} onChange={(e) => set("hero_video", e.target.value)} placeholder="https://...mp4" className="fin" />
            {form.hero_video ? <button onClick={() => set("hero_video", "")} className="text-xs text-zinc-400 hover:text-rose-500 whitespace-nowrap" data-testid="fp-hero-video-reset">Reset</button> : null}
          </div>
        </F>
        <F label="Stores section heading"><input data-testid="fp-stores-heading" value={form.stores_heading} onChange={(e) => set("stores_heading", e.target.value)} className="fin" /></F>
      </Card>

      {/* Template */}
      <Card title="Frontpage template">
        <p className="text-sm text-zinc-500 -mt-1 mb-3">Choose the look of your public landing page.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { key: "v1", name: "Classic — Light", desc: "Glassmorphism bento grid, soft light theme." },
            { key: "v2", name: "Bold — Dark / Lime", desc: "Editorial black background, giant wordmark, neon-lime accent." },
            { key: "v3", name: "Pop — Blue Collage", desc: "Y2K Gen-Z scrapbook: electric blue, lime + hot-pink stickers, chrome stars." },
            { key: "v4", name: "Editorial — Warm Serif", desc: "High-fashion print: alabaster + terracotta, Cormorant serif, flat 1px grid." },
          ].map((t) => (
            <button key={t.key} type="button" onClick={() => set("frontpage_template", t.key)} data-testid={`template-${t.key}`}
              className={`text-left border rounded-xl p-4 transition-all ${form.frontpage_template === t.key ? "border-black ring-2 ring-black" : "border-zinc-200 hover:border-zinc-400"}`}>
              <div className={`h-16 rounded-lg mb-3 flex items-center justify-center ${t.key === "v2" ? "bg-black" : t.key === "v1" ? "bg-gradient-to-br from-zinc-100 to-zinc-200" : ""}`}
                style={t.key === "v3" ? { background: "#1e3cff" } : t.key === "v4" ? { background: "#F4F3EF", border: "1px solid rgba(26,26,24,0.15)" } : {}}>
                {t.key === "v2" && <span style={{ fontFamily: "Anton", color: "#ccff00" }} className="text-lg tracking-wide">STOREIG</span>}
                {t.key === "v3" && <span style={{ fontFamily: "Anton" }} className="text-lg tracking-wide text-white">STORE<span style={{ color: "#ccff00" }}>IG</span></span>}
                {t.key === "v4" && <span style={{ fontFamily: "'Cormorant Garamond', serif", color: "#C25934" }} className="text-2xl">StoreIG</span>}
              </div>
              <div className="font-heading font-semibold text-sm">{t.name}</div>
              <div className="text-xs text-zinc-400">{t.desc}</div>
            </button>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="preview-launcher">
          <span className="text-xs text-zinc-500">Preview live (without saving):</span>
          {["v1", "v2", "v3", "v4"].map((k) => (
            <a key={k} href={`/?preview=${k}`} target="_blank" rel="noreferrer" data-testid={`preview-open-${k}`}
              className="inline-flex items-center gap-1 text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 hover:bg-zinc-50 transition-colors">
              {k.toUpperCase()}
            </a>
          ))}
        </div>
      </Card>

      {/* Homepage sections (v2 template) */}
      <Card title="Homepage sections — New stores, Categories & Easy sign up (v2)">
        <p className="text-sm text-zinc-500 -mt-1">These sections show on the Bold (v2) template. Assign a store's category & featured flag under Stores.</p>
        <F label="'New stores' heading"><input data-testid="fp-new-stores-heading" value={form.new_stores_heading || ""} onChange={(e) => set("new_stores_heading", e.target.value)} className="fin" /></F>
        <F label="'Find stores by category' heading"><input data-testid="fp-categories-heading" value={form.categories_heading || ""} onChange={(e) => set("categories_heading", e.target.value)} className="fin" /></F>
        <div className="border-t border-zinc-100 pt-4">
          <div className="text-sm font-heading font-semibold mb-3">Easy sign up section</div>
          <F label="Heading"><input data-testid="fp-signup-heading" value={form.signup_heading || ""} onChange={(e) => set("signup_heading", e.target.value)} className="fin" /></F>
          <F label="Subtitle"><textarea data-testid="fp-signup-subtitle" rows={2} value={form.signup_subtitle || ""} onChange={(e) => set("signup_subtitle", e.target.value)} className="fin" /></F>
          <F label="Video URL (blank = default hero video)">
            <div className="flex items-center gap-2">
              <input data-testid="fp-signup-video" value={form.signup_video || ""} onChange={(e) => set("signup_video", e.target.value)} placeholder="https://...mp4" className="fin" />
              {form.signup_video ? <button onClick={() => set("signup_video", "")} className="text-xs text-zinc-400 hover:text-rose-500 whitespace-nowrap" data-testid="fp-signup-video-reset">Reset</button> : null}
            </div>
          </F>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Steps</label>
          <div className="space-y-3">
            {(form.signup_steps || []).map((st, i) => (
              <div key={i} className="border border-zinc-200 rounded-xl p-3 space-y-2" data-testid={`fp-step-${i}`}>
                <div className="flex gap-2">
                  <input value={st.title} onChange={(e) => setStep(i, "title", e.target.value)} placeholder="Step title" className="fin" data-testid={`fp-step-title-${i}`} />
                  <button onClick={() => removeStep(i)} className="text-zinc-400 hover:text-rose-500 px-2" data-testid={`fp-step-remove-${i}`}><Trash2 size={16} /></button>
                </div>
                <textarea value={st.text} onChange={(e) => setStep(i, "text", e.target.value)} rows={2} placeholder="Step description" className="fin" data-testid={`fp-step-text-${i}`} />
              </div>
            ))}
            <button onClick={addStep} data-testid="fp-step-add" className="flex items-center gap-2 text-sm font-semibold border border-zinc-200 rounded-full px-4 py-2 hover:bg-zinc-50">
              <Plus size={14} /> Add step
            </button>
          </div>
        </div>
      </Card>

      {/* Pricing */}
      <Card title="Pricing (3 plans)">
        <div className="flex items-center justify-between border border-zinc-200 rounded-xl p-4 mb-2">
          <div>
            <div className="text-sm font-heading font-semibold">Stripe subscription checkout</div>
            <div className="text-xs text-zinc-400">When ON, paid-plan buttons start a real Stripe subscription. When OFF, they open the sign-up form.</div>
          </div>
          <button
            type="button" role="switch" aria-checked={!!form.subscriptions_enabled}
            onClick={() => set("subscriptions_enabled", !form.subscriptions_enabled)}
            data-testid="fp-subscriptions-toggle"
            className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${form.subscriptions_enabled ? "bg-black" : "bg-zinc-200"}`}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${form.subscriptions_enabled ? "translate-x-[22px]" : "translate-x-0.5"}`} />
          </button>
        </div>
        <F label="Section heading"><input data-testid="fp-pricing-heading" value={form.pricing_heading} onChange={(e) => set("pricing_heading", e.target.value)} className="fin" /></F>
        <div className="grid md:grid-cols-3 gap-4">
          {form.pricing.map((p, i) => (
            <div key={p.id || i} className="border border-zinc-200 rounded-xl p-4 space-y-3" data-testid={`fp-plan-${i}`}>
              <input value={p.name} onChange={(e) => setPrice(i, "name", e.target.value)} placeholder="Plan name" className="fin" data-testid={`fp-plan-name-${i}`} />
              <div className="grid grid-cols-2 gap-2">
                <input value={p.price} onChange={(e) => setPrice(i, "price", e.target.value)} placeholder="€0" className="fin" data-testid={`fp-plan-price-${i}`} />
                <input value={p.period} onChange={(e) => setPrice(i, "period", e.target.value)} placeholder="/mo" className="fin" />
              </div>
              <textarea
                value={(p.features || []).join("\n")}
                onChange={(e) => setPrice(i, "features", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                rows={4} placeholder="One feature per line" className="fin"
                data-testid={`fp-plan-features-${i}`}
              />
              <input value={p.cta} onChange={(e) => setPrice(i, "cta", e.target.value)} placeholder="Button label" className="fin" />
              <label className="flex items-center gap-2 text-sm text-zinc-600">
                <input type="checkbox" checked={!!p.highlight} onChange={(e) => setPrice(i, "highlight", e.target.checked)} data-testid={`fp-plan-highlight-${i}`} /> Highlight this plan
              </label>
            </div>
          ))}
        </div>
      </Card>

      {/* Reviews */}
      <Card title="Reviews slider">
        <F label="Section heading"><input data-testid="fp-reviews-heading" value={form.reviews_heading} onChange={(e) => set("reviews_heading", e.target.value)} className="fin" /></F>
        <div className="space-y-4">
          {form.reviews.map((r, i) => (
            <div key={i} className="border border-zinc-200 rounded-xl p-4 space-y-2" data-testid={`fp-review-${i}`}>
              <div className="flex gap-2">
                <input value={r.name} onChange={(e) => setReview(i, "name", e.target.value)} placeholder="Name" className="fin" data-testid={`fp-review-name-${i}`} />
                <input value={r.handle} onChange={(e) => setReview(i, "handle", e.target.value)} placeholder="@handle" className="fin" />
                <select value={r.rating} onChange={(e) => setReview(i, "rating", parseInt(e.target.value, 10))} className="fin w-24">
                  {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}★</option>)}
                </select>
                <button onClick={() => removeReview(i)} className="text-zinc-400 hover:text-rose-500 px-2" data-testid={`fp-review-remove-${i}`}><Trash2 size={16} /></button>
              </div>
              <textarea value={r.text} onChange={(e) => setReview(i, "text", e.target.value)} rows={2} placeholder="Review text" className="fin" data-testid={`fp-review-text-${i}`} />
            </div>
          ))}
          <button onClick={addReview} data-testid="fp-review-add" className="flex items-center gap-2 text-sm font-semibold border border-zinc-200 rounded-full px-4 py-2 hover:bg-zinc-50">
            <Plus size={14} /> Add review
          </button>
        </div>
      </Card>

      {/* SEO — landing / main page only */}
      <Card title="SEO — landing & main page">
        <p className="text-xs text-zinc-400 -mt-2">Controls the browser tab title and how the main landing page appears in Google and social shares. Individual shops have their own SEO settings and are not affected.</p>
        <F label="Site title (browser tab & Google)">
          <input data-testid="fp-meta-title" value={form.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} maxLength={70} className="fin" />
          <div className="text-[11px] text-zinc-400 mt-1">{(form.meta_title || "").length}/70</div>
        </F>
        <F label="Meta description (Google snippet)">
          <textarea data-testid="fp-meta-description" rows={3} value={form.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} maxLength={200} className="fin" />
          <div className="text-[11px] text-zinc-400 mt-1">{(form.meta_description || "").length}/200 · aim for 150–160 characters</div>
        </F>
        <F label="Social share image (Open Graph — 1200×630 recommended)">
          <div className="flex items-center gap-3">
            <div className="h-14 w-24 rounded-lg overflow-hidden bg-zinc-100 shrink-0 flex items-center justify-center">
              {form.og_image ? <img src={imgUrl(form.og_image)} alt="" className="h-full w-full object-cover" /> : <span className="text-[10px] text-zinc-400">None</span>}
            </div>
            <input ref={ogFileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadOg(e.target.files[0])} data-testid="fp-og-image-input" />
            <button onClick={() => ogFileRef.current.click()} className="flex items-center gap-2 border border-zinc-200 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-zinc-50" data-testid="fp-og-image-upload">
              {ogUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
            </button>
            {form.og_image ? <button onClick={() => set("og_image", "")} data-testid="fp-og-image-remove" className="text-xs text-zinc-400 hover:text-rose-500">Remove</button> : null}
          </div>
          <input data-testid="fp-og-image-url" value={form.og_image || ""} onChange={(e) => set("og_image", e.target.value)} placeholder="…or paste an image URL" className="fin mt-2" />
        </F>
        <F label="Favicon / site icon (PNG, 512×512 recommended)">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg overflow-hidden bg-zinc-100 shrink-0 flex items-center justify-center">
              {form.favicon ? <img src={imgUrl(form.favicon)} alt="" className="h-full w-full object-contain" /> : <span className="text-[9px] text-zinc-400">None</span>}
            </div>
            <input ref={faviconFileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadFavicon(e.target.files[0])} data-testid="fp-favicon-input" />
            <button onClick={() => faviconFileRef.current.click()} className="flex items-center gap-2 border border-zinc-200 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-zinc-50" data-testid="fp-favicon-upload">
              {faviconUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
            </button>
            {form.favicon ? <button onClick={() => set("favicon", "")} data-testid="fp-favicon-remove" className="text-xs text-zinc-400 hover:text-rose-500">Remove</button> : null}
          </div>
        </F>

        {/* Live preview */}
        <div className="border-t border-zinc-100 pt-4">
          <div className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Preview</div>
          <div className="rounded-xl border border-zinc-200 p-4 bg-white max-w-xl" data-testid="seo-preview-google">
            <div className="text-[11px] text-zinc-400 mb-2">Google search result</div>
            <div className="flex items-center gap-2 mb-1">
              {form.favicon ? <img src={imgUrl(form.favicon)} alt="" className="h-5 w-5 rounded-full border border-zinc-200 object-contain" /> : <div className="h-5 w-5 rounded-full bg-zinc-200" />}
              <div className="leading-tight">
                <div className="text-xs text-zinc-800">StoreIG</div>
                <div className="text-[11px] text-zinc-500">my.storeig.com</div>
              </div>
            </div>
            <div className="text-[#1a0dab] text-xl leading-snug truncate" data-testid="seo-preview-title">{form.meta_title || "StoreIG"}</div>
            <div className="text-[#4d5156] text-sm leading-snug line-clamp-2" data-testid="seo-preview-desc">{form.meta_description || "Add a meta description to control how this snippet reads in Google."}</div>
          </div>

          <div className="rounded-xl border border-zinc-200 overflow-hidden bg-white max-w-md mt-4" data-testid="seo-preview-social">
            <div className="text-[11px] text-zinc-400 px-3 pt-3">Social share card</div>
            <div className="mx-3 my-2 rounded-lg overflow-hidden border border-zinc-100">
              <div className="aspect-[1200/630] bg-zinc-100 flex items-center justify-center overflow-hidden">
                {form.og_image ? <img src={imgUrl(form.og_image)} alt="" className="w-full h-full object-cover" data-testid="seo-preview-ogimage" /> : <span className="text-xs text-zinc-400 px-4 text-center">No share image — add one (1200×630)</span>}
              </div>
              <div className="px-3 py-2 border-t border-zinc-100 bg-zinc-50">
                <div className="text-[10px] uppercase tracking-wide text-zinc-400">my.storeig.com</div>
                <div className="text-sm font-semibold text-zinc-800 truncate">{form.meta_title || "StoreIG"}</div>
                <div className="text-xs text-zinc-500 line-clamp-1">{form.meta_description}</div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Disclaimer & footer */}
      <Card title="Footer & disclaimer">
        <F label="Footer tagline"><input data-testid="fp-footer" value={form.footer_tagline} onChange={(e) => set("footer_tagline", e.target.value)} className="fin" /></F>
        <F label="Legal disclaimer (bottom of page)"><textarea data-testid="fp-disclaimer" rows={4} value={form.disclaimer} onChange={(e) => set("disclaimer", e.target.value)} className="fin" /></F>
        <div className="border-t border-zinc-100 pt-4">
          <div className="text-sm font-heading font-semibold mb-3">Brand — "A brand by …"</div>
          <div className="grid grid-cols-2 gap-4">
            <F label="Brand name"><input data-testid="fp-brand-name" value={form.brand_name || ""} onChange={(e) => set("brand_name", e.target.value)} className="fin" /></F>
            <F label="Brand link URL"><input data-testid="fp-brand-url" value={form.brand_url || ""} onChange={(e) => set("brand_url", e.target.value)} placeholder="https://" className="fin" /></F>
          </div>
          <F label="Brand logo (blank = show name as text)">
            <div className="flex items-center gap-3">
              <div className="h-12 w-24 rounded-lg overflow-hidden bg-zinc-100 shrink-0 flex items-center justify-center">
                {form.brand_logo_url ? <img src={imgUrl(form.brand_logo_url)} alt="" className="h-full w-full object-contain" /> : <span className="text-[10px] text-zinc-400">None</span>}
              </div>
              <input ref={brandFileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadBrand(e.target.files[0])} data-testid="fp-brand-logo-input" />
              <button onClick={() => brandFileRef.current.click()} className="flex items-center gap-2 border border-zinc-200 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-zinc-50" data-testid="fp-brand-logo-upload">
                {brandUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
              </button>
              {form.brand_logo_url ? <button onClick={() => set("brand_logo_url", "")} data-testid="fp-brand-logo-remove" className="text-xs text-zinc-400 hover:text-rose-500">Remove</button> : null}
            </div>
          </F>
        </div>
      </Card>

      <button onClick={save} disabled={saving} data-testid="fp-save-bottom" className="casper-btn rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
        {saving && <Loader2 size={14} className="animate-spin" />} Save changes
      </button>

      <style>{`.fin{width:100%;border:1px solid #e4e4e7;border-radius:12px;padding:10px 14px;font-size:14px;outline:none;font-family:'Plus Jakarta Sans'}.fin:focus{border-color:#4F46E5}`}</style>
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="bg-white border border-zinc-200/80 rounded-xl p-6 space-y-5 mb-6">
    <h2 className="font-heading font-medium text-lg tracking-tight">{title}</h2>
    {children}
  </div>
);

const F = ({ label, children }) => (
  <div>
    <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">{label}</label>
    {children}
  </div>
);
